<script>
/**
 * @component LiquidationSubmissionDetail
 * @description UC-REG-169 — Detalle del submission (flujo B).
 *              Muestra header + totales + tabla por línea con acciones del
 *              auditor (Aceptar / Rechazar / Débito parcial). Polling cada
 *              2s mientras el submission está en 'processing'.
 * @example <LiquidationSubmissionDetail />
 */
import * as XLSX from "xlsx";
import submissionSvc from "@/services/liquidation-submission";
import LiquidationSubmissionInvoiceTab from "./LiquidationSubmissionInvoiceTab.vue";

const POLL_MS = 2000;

const STATUS_LABELS = {
  draft: "Borrador",
  processing: "Procesando…",
  // UC-REG-196 — 'reviewing' = evaluado, esperando confirmación del operador.
  //   NO se han creado TRXs todavía. Se hace efectivo al tocar "Aceptar".
  reviewing: "En revisión",
  resolved: "Confirmada",
  reverted: "Anulada",
  failed: "Falló",
};
const STATUS_CHIP = {
  draft: "chip--neutral",
  processing: "chip--info",
  reviewing: "chip--warn",
  resolved: "chip--ok",
  reverted: "chip--warn",
  failed: "chip--err",
};

// Categorización del status del motor (columna "Motor") → chip
const MOTOR_LABEL = {
  matched: "Matched",
  validated_ex_post: "Ex-post",
  rejected_engine: "Motor",
  rejected_rules: "Reglas",
  rejected_price: "Precio",
  rejected_code: "Código",
  rejected_member: "Afiliado",
  // UC-REG-178 Fase 1 — línea que apunta a una TRX ya liquidada.
  rejected_duplicate: "Duplicada",
  // UC-REG-192 — nuevos motivos con causa raíz específica.
  rejected_contract_not_active: "Sin contrato",
  rejected_no_price: "Sin precio",
  needs_review: "Revisar",
  ok_manual_override: "Override",
  // UC-REG-187 — línea anulada por operador (mal cargada en import).
  voided: "Anulada",
  pending: "Pendiente",
};
const MOTOR_CHIP = {
  matched: "chip--ok",
  validated_ex_post: "chip--info",
  rejected_engine: "chip--err",
  rejected_rules: "chip--err",
  rejected_price: "chip--err",
  rejected_code: "chip--err",
  rejected_member: "chip--err",
  rejected_duplicate: "chip--err",
  rejected_contract_not_active: "chip--err",
  rejected_no_price: "chip--err",
  needs_review: "chip--warn",
  ok_manual_override: "chip--warn",
  voided: "chip--neutral",
  pending: "chip--neutral",
};

/**
 * @description Deriva la decisión visual actual de una línea a partir del
 * estado + montos. Devuelve 'accept' | 'reject' | 'debit'.
 * @param {Object} line
 * @returns {string}
 */
function currentDecision(line) {
  if (!line) return "";
  // UC-REG-187 — voided = línea anulada por operador. Diferente de rejected.
  if (line.status === "voided") return "void";
  const rejected = [
    "rejected_engine",
    "rejected_rules",
    "rejected_price",
    "rejected_code",
    "rejected_member",
    "rejected_duplicate",
    "rejected_contract_not_active",
    "rejected_no_price",
  ];
  if (rejected.includes(line.status)) return "debit"; // sugerido por default
  if (line.status === "ok_manual_override") {
    if ((line.acceptedAmount || 0) === 0) return "reject";
    if ((line.debitAmount || 0) > 0) return "debit";
    return "accept";
  }
  return "accept"; // matched / validated_ex_post / pending
}

export default {
  name: "LiquidationSubmissionDetail",
  components: { LiquidationSubmissionInvoiceTab },
  data() {
    return {
      submission: null,
      loading: true,
      polling: null,
      // UC-REG-196 — flag mientras se confirma el submission
      confirming: false,
      // UC-REG-180 Fase 2 — tabs internos del detail
      activeTab: "lines",
      // Filtros
      filterStatus: "",
      searchInput: "",
      // Modal override
      overrideModalOpen: false,
      overrideLine: null,
      // UC-REG-210 — Modal buscar match manual
      matchModalOpen: false,
      matchLine: null,
      matchCandidates: [],
      matchLoading: false,
      selectedCandidateId: null,
      linkLoading: false,
      overrideDecision: "",
      overrideDebitAmount: 0,
      overrideReason: "",
      // Modal revert
      revertModalOpen: false,
      revertReason: "",
      revertLoading: false,
      // UC-REG-230 — Selección múltiple para aceptar en bloque
      selectedLineIds: [],
      bulkAccepting: false,
    };
  },
  computed: {
    statusLabels() {
      return STATUS_LABELS;
    },
    statusChip() {
      return STATUS_CHIP;
    },
    motorLabel() {
      return MOTOR_LABEL;
    },
    motorChip() {
      return MOTOR_CHIP;
    },
    /** @returns {Array} líneas filtradas para renderizar */
    filteredLines() {
      if (!this.submission) return [];
      let lines = this.submission.lines || [];
      if (this.filterStatus) {
        if (this.filterStatus === "rejected_motor") {
          lines = lines.filter((l) =>
            [
              "rejected_engine",
              "rejected_rules",
              "rejected_price",
              "rejected_code",
              "rejected_member",
              "rejected_duplicate",
              "rejected_contract_not_active",
              "rejected_no_price",
            ].includes(l.status)
          );
        } else if (this.filterStatus === "override") {
          lines = lines.filter((l) => l.status === "ok_manual_override");
        } else if (this.filterStatus === "voided") {
          lines = lines.filter((l) => l.status === "voided");
        }
      }
      if (this.searchInput) {
        const q = this.searchInput.toLowerCase();
        lines = lines.filter(
          (l) =>
            (l.declaredCode || "").toLowerCase().includes(q) ||
            (l.declaredMemberIdentifier || "").toLowerCase().includes(q)
        );
      }
      return lines;
    },
    /**
     * @returns {Array} UC-REG-230 — líneas visibles seleccionables (excluye
     *   las ya anuladas; el resto se puede marcar para aceptar en bloque).
     */
    selectableLines() {
      return this.filteredLines.filter((l) => l.status !== "voided");
    },
    /**
     * @returns {boolean} UC-REG-230 — true si TODAS las líneas seleccionables
     *   visibles están marcadas (para el checkbox del header).
     */
    allVisibleSelected() {
      const ids = this.selectableLines.map((l) => l._id);
      return (
        ids.length > 0 && ids.every((id) => this.selectedLineIds.includes(id))
      );
    },
    totals() {
      return (
        this.submission?.totals || {
          linesTotal: 0,
          linesAccepted: 0,
          linesRejected: 0,
          declaredAmount: 0,
          acceptedAmount: 0,
          debitAmount: 0,
        }
      );
    },
    isProcessing() {
      return this.submission?.status === "processing";
    },
    isReverted() {
      return this.submission?.status === "reverted";
    },
    canEdit() {
      return this.submission && !this.isProcessing && !this.isReverted;
    },
    /** @returns {string} Header title — evita el `#` dentro de {{}} de Pug */
    headerTitle() {
      if (!this.submission) return "";
      const s = this.submission;
      const provider =
        (s.providerSnapshot &&
          `${s.providerSnapshot.lastName || ""}, ${
            s.providerSnapshot.firstName || ""
          }`
            .replace(/^, /, "")
            .trim()) ||
        "Prestador";
      const period = `${String(s.billingPeriod.month).padStart(2, "0")}/${
        s.billingPeriod.year
      }`;
      return `Submission #${s._id.substr(-6)} · ${provider} · ${period}`;
    },
    /** @returns {string} Encabezado del modal de override */
    overrideModalHeader() {
      if (!this.overrideLine) return "";
      return `Línea #${this.overrideLine.lineNumber} · Código ${
        this.overrideLine.declaredCode
      } · Declarado ${this.formatMoney(this.overrideLine.declaredAmount)}`;
    },

    /**
     * @returns {boolean} UC-REG-178 Fase 1 — true si accept está bloqueado
     *   para esta línea (rejected_member o rejected_duplicate).
     */
    isBlockedForAccept() {
      if (!this.overrideLine) return false;
      return ["rejected_member", "rejected_duplicate"].includes(
        this.overrideLine.status
      );
    },
  },
  created() {
    this.fetch();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  watch: {
    isProcessing(v) {
      if (v) this.startPolling();
      else this.stopPolling();
    },
  },
  methods: {
    async fetch() {
      try {
        this.submission = await submissionSvc.getById(this.$route.params.id);
        if (this.isProcessing && !this.polling) this.startPolling();
      } catch (err) {
        this.$message.error("Error al cargar el submission");
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
    startPolling() {
      if (this.polling) return;
      this.polling = setInterval(async () => {
        try {
          this.submission = await submissionSvc.getById(this.$route.params.id);
          if (!this.isProcessing) this.stopPolling();
        } catch (e) {
          /* silently retry */
        }
      }, POLL_MS);
    },
    stopPolling() {
      if (this.polling) {
        clearInterval(this.polling);
        this.polling = null;
      }
    },
    /** @method decisionOf - Decisión visual actual de una línea */
    decisionOf(line) {
      return currentDecision(line);
    },
    /**
     * @method canOfferMatch - UC-REG-210. La línea puede ofrecerse a match
     *   manual si el motor la dejó como ex-post o como algún rechazo
     *   recuperable (no rejected_member, no rejected_duplicate).
     * @param {Object} line
     * @returns {boolean}
     */
    canOfferMatch(line) {
      if (!this.canEdit) return false;
      const s = line && line.status;
      return (
        s === "validated_ex_post" ||
        s === "rejected_engine" ||
        s === "rejected_code" ||
        s === "rejected_contract_not_active" ||
        s === "rejected_no_price" ||
        s === "rejected_price"
      );
    },

    /**
     * @method openMatchModal - UC-REG-210. Abre popup + carga candidatas.
     * @param {Object} line
     */
    async openMatchModal(line) {
      this.matchLine = line;
      this.selectedCandidateId = null;
      this.matchCandidates = [];
      this.matchModalOpen = true;
      this.matchLoading = true;
      try {
        const res = await submissionSvc.matchCandidates(
          this.submission._id,
          line._id
        );
        this.matchCandidates = res.candidates || [];
      } catch (e) {
        this.$message.error(
          (e && e.response && e.response.data && e.response.data.error) ||
            "Error al cargar candidatas"
        );
      } finally {
        this.matchLoading = false;
      }
    },

    /**
     * @method submitManualMatch - UC-REG-210. Vincula la línea con la TRX
     *   seleccionada. Al terminar recarga el submission.
     */
    async submitManualMatch() {
      if (!this.selectedCandidateId || !this.matchLine) return;
      this.linkLoading = true;
      try {
        await submissionSvc.manualMatch(
          this.submission._id,
          this.matchLine._id,
          this.selectedCandidateId
        );
        this.$message.success("Línea vinculada");
        this.matchModalOpen = false;
        await this.fetch();
      } catch (e) {
        this.$message.error(
          (e && e.response && e.response.data && e.response.data.error) ||
            "Error al vincular"
        );
      } finally {
        this.linkLoading = false;
      }
    },

    /**
     * @method rejectLineFromModal - UC-REG-210. Rechaza la línea directo
     *   desde el modal (atajo al PATCH decision=reject existente).
     */
    async rejectLineFromModal() {
      if (!this.matchLine) return;
      try {
        await submissionSvc.overrideLine(
          this.submission._id,
          this.matchLine._id,
          {
            decision: "reject",
            reason: "Rechazada desde búsqueda de match manual",
          }
        );
        this.$message.success("Línea rechazada");
        this.matchModalOpen = false;
        await this.fetch();
      } catch (e) {
        this.$message.error(
          (e && e.response && e.response.data && e.response.data.error) ||
            "Error al rechazar"
        );
      }
    },

    /**
     * @method formatShortTime - UC-REG-210. HH:mm.
     * @param {string|Date} v
     * @returns {string}
     */
    formatShortTime(v) {
      if (!v) return "";
      try {
        return new Date(v).toLocaleTimeString("es-AR", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });
      } catch {
        return "";
      }
    },

    /**
     * @method memberName - UC-REG-209. Nombre del afiliado a mostrar:
     *   prioriza matchedMember (User populated), después patientFullName
     *   del import.
     * @param {Object} line
     * @returns {string}
     */
    memberName(line) {
      const m = line && line.matchedMember;
      if (m && (m.firstName || m.lastName)) {
        return `${m.lastName || ""}, ${m.firstName || ""}`
          .replace(/^, /, "")
          .trim();
      }
      return (line && line.patientFullName) || "";
    },

    /** @method suggestedDebit - Débito sugerido: declarado − aceptado */
    suggestedDebit(line) {
      // UC-REG-233 — líneas ACEPTADAS-con-débito ya tienen el débito real en
      //   line.debitAmount (ej. sobre-declaración UC-REG-229 que queda
      //   'matched', o débito manual 'ok_manual_override'). Mostrar ese valor:
      //   antes devolvía 0 para 'matched' → la columna Déb. sugerido quedaba
      //   en $0 aunque el total sí lo contaba.
      if (
        Number(line.debitAmount) > 0 &&
        ["matched", "validated_ex_post", "ok_manual_override"].includes(
          line.status
        )
      ) {
        return Number(line.debitAmount);
      }
      const declared = Number(line.declaredAmount) || 0;
      const contract = Number(line.contractAmount) || 0;
      if (line.status === "rejected_price" && contract) {
        return Math.max(0, declared - contract);
      }
      if (
        [
          "rejected_engine",
          "rejected_rules",
          "rejected_code",
          "rejected_member",
          "rejected_contract_not_active",
          "rejected_no_price",
          // UC-REG-196 — Duplicada también sugiere debitar el declared
          //   completo. El prestador está pidiendo cobrar algo que ya
          //   se pagó en un submission anterior.
          "rejected_duplicate",
        ].includes(line.status)
      ) {
        return declared;
      }
      return 0;
    },
    /** @method openOverride - Abre modal de override con defaults */
    openOverride(line, decision) {
      if (!this.canEdit) return;
      this.overrideLine = line;
      this.overrideDecision = decision;
      this.overrideDebitAmount =
        decision === "debit"
          ? this.suggestedDebit(line) || Number(line.declaredAmount) || 0
          : 0;
      this.overrideReason = "";
      this.overrideModalOpen = true;
    },
    async submitOverride() {
      if (!this.overrideReason.trim()) {
        this.$message.warning("El motivo es obligatorio");
        return;
      }
      try {
        await submissionSvc.overrideLine(
          this.submission._id,
          this.overrideLine._id,
          {
            decision: this.overrideDecision,
            debitAmount:
              this.overrideDecision === "debit"
                ? Number(this.overrideDebitAmount) || 0
                : undefined,
            reason: this.overrideReason.trim(),
          }
        );
        this.$message.success("Decisión actualizada");
        this.overrideModalOpen = false;
        await this.fetch();
      } catch (err) {
        const body = err.response?.data;
        const detail =
          body?.error ||
          body?.message ||
          (typeof body === "string" ? body : "") ||
          err.message;
        this.$message.error(`Error al aplicar override: ${detail}`);
      }
    },
    /** @method isLineSelected — UC-REG-230 */
    isLineSelected(id) {
      return this.selectedLineIds.includes(id);
    },
    /** @method toggleLine — UC-REG-230. Marca/desmarca una línea. */
    toggleLine(id) {
      const i = this.selectedLineIds.indexOf(id);
      if (i === -1) this.selectedLineIds.push(id);
      else this.selectedLineIds.splice(i, 1);
    },
    /**
     * @method toggleSelectAll — UC-REG-230. Marca/desmarca todas las
     *   líneas seleccionables visibles.
     * @param {boolean} val
     */
    toggleSelectAll(val) {
      this.selectedLineIds = val ? this.selectableLines.map((l) => l._id) : [];
    },
    /**
     * @method bulkAccept — UC-REG-230. Acepta en bloque las líneas
     *   seleccionadas (decision='accept' vía el override por línea, con un
     *   motivo compartido). Las líneas ya aceptadas quedan igual; las
     *   bloqueadas (afiliado inexistente / duplicado) se saltean e informan.
     *   La confirmación final (crear TRXs) sigue siendo el botón ACEPTAR.
     */
    async bulkAccept() {
      if (!this.canEdit || this.selectedLineIds.length === 0) return;
      const reason = "Aceptación en bloque";
      const lines = (this.submission.lines || []).filter((l) =>
        this.selectedLineIds.includes(l._id)
      );
      const blocked = lines.filter((l) =>
        ["rejected_member", "rejected_duplicate"].includes(l.status)
      );
      const toAccept = lines.filter(
        (l) => !["rejected_member", "rejected_duplicate"].includes(l.status)
      );
      if (toAccept.length === 0) {
        this.$message.warning(
          "Las líneas seleccionadas no se pueden aceptar (afiliado inexistente o duplicado)."
        );
        return;
      }
      this.bulkAccepting = true;
      let ok = 0;
      let failed = 0;
      // eslint-disable-next-line no-restricted-syntax
      for (const line of toAccept) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await submissionSvc.overrideLine(this.submission._id, line._id, {
            decision: "accept",
            reason,
          });
          ok += 1;
        } catch (e) {
          failed += 1;
        }
      }
      this.selectedLineIds = [];
      await this.fetch();
      this.bulkAccepting = false;
      let msg = `${ok} línea(s) aceptada(s)`;
      if (blocked.length) msg += ` · ${blocked.length} salteada(s) (bloqueada)`;
      if (failed) msg += ` · ${failed} con error`;
      if (failed || blocked.length) this.$message.warning(msg);
      else this.$message.success(msg);
    },
    async revert() {
      if (!this.revertReason.trim()) {
        this.$message.warning("Ingresá el motivo del revert");
        return;
      }
      this.revertLoading = true;
      try {
        await submissionSvc.revert(
          this.submission._id,
          this.revertReason.trim()
        );
        this.$message.success("Submission revertido");
        this.revertModalOpen = false;
        await this.fetch();
      } catch (err) {
        this.$message.error(err.response?.data?.error || "Error al revertir");
      } finally {
        this.revertLoading = false;
      }
    },
    /**
     * @method confirmSubmission — UC-REG-196. Materializa las líneas
     *   aceptadas del submission (crea SRs + LiquidationItems + propaga
     *   liquidationStatus). Pasa el submission a 'resolved'.
     */
    async confirmSubmission() {
      try {
        await this.$confirm(
          "¿Confirmar la liquidación? Se crearán las TRXs para todas las líneas aceptadas. Esta acción no se puede deshacer con un click — para revertir hay que anular todo el submission.",
          "Confirmar liquidación",
          {
            type: "warning",
            confirmButtonText: "Aceptar y crear TRXs",
            cancelButtonText: "Cancelar",
          }
        );
      } catch {
        return;
      }
      this.confirming = true;
      try {
        const res = await submissionSvc.confirm(this.submission._id);
        this.$message.success(
          `Liquidación confirmada · ${res.stats?.created || 0} TRXs nuevas · ${
            res.stats?.updated || 0
          } actualizadas`
        );
        await this.fetch();
      } catch (err) {
        this.$message.error(
          err.response?.data?.error || "Error al confirmar liquidación"
        );
      } finally {
        this.confirming = false;
      }
    },
    async reprocess() {
      try {
        await submissionSvc.reprocess(this.submission._id);
        this.$message.success("Reprocesamiento iniciado");
        await this.fetch();
      } catch (err) {
        this.$message.error(err.response?.data?.error || "Error al reprocesar");
      }
    },
    formatDate(d) {
      if (!d) return "-";
      return new Date(d).toLocaleString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    },
    formatShortDate(d) {
      if (!d) return "-";
      return new Date(d).toLocaleDateString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      });
    },
    formatMoney(n) {
      if (n == null) return "-";
      return `$${Number(n).toLocaleString("es-AR")}`;
    },
    goBack() {
      this.$router.push({ name: "liquidation-submission-list" });
    },
    /**
     * @method exportExcel — UC-REG-234. Exporta el lote a Excel para pasar
     *   al ERP: cabecera (LIQ, prestador, período, estado, totales) + una
     *   fila por prestación (afiliado, código, prestación, contrato,
     *   declarado, débito, aceptado, motor, decisión) + fila TOTAL.
     *   v1 con los datos que ya genera el pipeline.
     */
    exportExcel() {
      const s = this.submission;
      if (!s) return;
      const provider =
        (s.providerSnapshot &&
          `${s.providerSnapshot.lastName || ""}, ${
            s.providerSnapshot.firstName || ""
          }`
            .replace(/^, /, "")
            .trim()) ||
        "";
      const period = `${String(s.billingPeriod.month).padStart(2, "0")}/${
        s.billingPeriod.year
      }`;
      const liq = s.operationCode || `#${s._id.substr(-6)}`;
      const decisionLabel = {
        accept: "Aceptado",
        reject: "Rechazado",
        debit: "Débito",
        void: "Anulada",
      };
      const meta = [
        ["Liquidación", liq],
        ["Prestador", provider],
        ["Período", period],
        ["Estado", this.statusLabels[s.status] || s.status],
        ["Monto aceptado", this.totals.acceptedAmount || 0],
        ["Débito total", this.totals.debitAmount || 0],
        [],
      ];
      const headers = [
        "#",
        "Afiliado",
        "DNI",
        "Nº Afiliado",
        "Código",
        "Prestación",
        "Fecha",
        "Contrato",
        "Declarado",
        "Débito",
        "Aceptado",
        "Motor",
        "Decisión",
      ];
      const rows = (s.lines || []).map((l) => [
        l.lineNumber,
        this.memberName(l) || "",
        l.declaredDni || "",
        l.declaredMemberNumber || "",
        l.declaredCode || "",
        l.declaredName || "",
        this.formatShortDate(l.performedAt),
        Number(l.contractAmount) || 0,
        Number(l.declaredAmount) || 0,
        this.suggestedDebit(l) || 0,
        Number(l.acceptedAmount) || 0,
        this.motorLabel[l.status] || l.status,
        decisionLabel[this.decisionOf(l)] || "",
      ]);
      const totalRow = [
        "",
        "",
        "",
        "",
        "",
        "",
        "TOTAL",
        "",
        "",
        this.totals.debitAmount || 0,
        this.totals.acceptedAmount || 0,
        "",
        "",
      ];
      const aoa = [...meta, headers, ...rows, [], totalRow];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Liquidación");
      const safeLiq = String(liq).replace(/[^A-Za-z0-9-]/g, "");
      XLSX.writeFile(
        wb,
        `liquidacion_${safeLiq}_${period.replace("/", "-")}.xlsx`
      );
    },
  },
};
</script>

<template lang="pug">
.subm-detail(v-loading="loading")
  //- Header
  .subm-detail__head(v-if="submission")
    div
      el-button.back(size="mini" icon="el-icon-back" @click="goBack") Volver
      h2.title {{ headerTitle }}
      .meta
        span Cargado {{ formatDate(submission.submittedAt) }}
        span.dot ·
        span {{ (submission.totals && submission.totals.linesTotal) || submission.lines.length }} líneas
        span.dot ·
        span.chip(:class="statusChip[submission.status]") {{ statusLabels[submission.status] }}
    .actions
      el-button(size="small" icon="el-icon-refresh" @click="reprocess" v-if="submission.status === 'failed'") Reprocesar
      //- UC-REG-196 — botón ACEPTAR grande solo en 'reviewing'. Al click
      //- materializa TRXs para líneas aceptadas y pasa a 'resolved'.
      el-button.subm-detail__accept-btn(
        v-if="submission.status === 'reviewing'"
        type="primary"
        size="small"
        icon="el-icon-check"
        :loading="confirming"
        @click="confirmSubmission"
      ) ACEPTAR
      //- UC-REG-234 — exportar el lote a Excel (para pasar al ERP)
      el-button(size="small" icon="el-icon-download" @click="exportExcel") Exportar
      el-button(size="small" type="danger" plain @click="revertModalOpen = true" :disabled="!canEdit") Anular liquidación

  //- Progress bar cuando está procesando
  .subm-detail__progress(v-if="submission && isProcessing")
    p.muted El submission está siendo procesado — la vista se refresca automáticamente cada 2s
    el-progress(:percentage="99" :indeterminate="true" status="warning")

  //- Failed message
  .subm-detail__failed(v-if="submission && submission.status === 'failed'")
    el-alert(
      title="El procesamiento falló"
      :description="submission.processingError || 'Error desconocido'"
      type="error" :closable="false" show-icon
    )

  //- UC-REG-196 — banner cuando submission está en revisión
  .subm-detail__reviewing(v-if="submission && submission.status === 'reviewing'")
    el-alert(
      title="Liquidación en revisión — pendiente de tu confirmación"
      description="Todavía NO se crearon TRXs. Revisá las decisiones línea por línea y tocá ACEPTAR arriba a la derecha para confirmar y crear las TRXs. Podés anular en cualquier momento."
      type="warning" :closable="false" show-icon
    )

  //- Anulada message (UC-REG-186 — anular ≠ debitar)
  .subm-detail__reverted(v-if="submission && isReverted")
    el-alert(
      :title="'Liquidación anulada · Motivo: ' + (submission.revertReason || '—')"
      description="La liquidación se borró como si no hubiera existido. Los montos declarados no cuentan como débito. Las TRXs preexistentes vuelven a estado Pendiente y las creadas por esta importación quedan rechazadas."
      type="warning" :closable="false" show-icon
    )

  //- Stats
  .subm-detail__stats(v-if="submission && submission.totals")
    .stat
      .stat__label Total líneas
      .stat__value {{ totals.linesTotal }}
    .stat
      .stat__label Aceptadas
      .stat__value.stat__value--ok {{ totals.linesAccepted }}
    .stat
      .stat__label Rechazadas / Débito
      .stat__value.stat__value--err {{ totals.linesRejected }}
    .stat
      .stat__label Monto aceptado
      .stat__value.stat__value--ok {{ formatMoney(totals.acceptedAmount) }}

  //- UC-REG-180 Fase 2 — Tabs Prestaciones / Factura
  .subm-detail__tabs(v-if="submission")
    .subm-detail__tab(
      :class="{ 'is-active': activeTab === 'lines' }"
      @click="activeTab = 'lines'"
    )
      | Prestaciones
      span.subm-detail__tab-count  ({{ totals.linesTotal || 0 }})
    .subm-detail__tab(
      :class="{ 'is-active': activeTab === 'invoice' }"
      @click="activeTab = 'invoice'"
      v-if="submission.status === 'resolved'"
    ) Factura y pago

  //- Tab: Factura y pago
  LiquidationSubmissionInvoiceTab(
    v-if="submission && activeTab === 'invoice'"
    :submission-id="submission._id"
    :accepted-amount="totals.acceptedAmount || 0"
    :can-edit="canEdit"
    @updated="fetch"
  )

  //- Filters — solo tab Prestaciones
  .subm-detail__filters(v-if="submission && activeTab === 'lines'")
    el-select(v-model="filterStatus" clearable placeholder="Todos los estados" size="small" style="width:220px")
      el-option(label="Rechazadas por motor" value="rejected_motor")
      el-option(label="Con override manual" value="override")
      el-option(label="Anuladas por operador" value="voided")
    el-input(v-model="searchInput" size="small" placeholder="Buscar código o afiliado…" prefix-icon="el-icon-search" style="flex:1")

  //- UC-REG-230 — Barra de acción para aceptar en bloque las seleccionadas.
  .subm-detail__bulkbar(v-if="submission && activeTab === 'lines' && canEdit && selectedLineIds.length")
    span.subm-detail__bulkbar-count {{ selectedLineIds.length }} seleccionada(s)
    el-button(size="small" type="primary" :loading="bulkAccepting" @click="bulkAccept") Aceptar seleccionadas ({{ selectedLineIds.length }})
    el-button(size="small" type="text" @click="selectedLineIds = []") Limpiar

  //- Tabla líneas
  .subm-detail__wrap(v-if="submission && activeTab === 'lines'")
    table.subm-detail__table
      thead
        tr
          //- UC-REG-230 — checkbox seleccionar todas (solo si editable)
          th.sel(v-if="canEdit")
            el-checkbox(:value="allVisibleSelected" @change="toggleSelectAll")
          th.n #
          //- UC-REG-209 — "Paciente" pasó a "Afiliado". Se muestra nombre
          //- + DNI + Nº afiliado si están disponibles.
          th Afiliado
          //- UC-REG-209 — Muestra código + denominación de la prestación
          //- (antes solo mostraba códigos).
          th Prestación
          //- UC-REG-209 — Nueva columna Contrato = monto contratado para
          //- la práctica (para comparar visualmente contra Declarado).
          th.amt.grp.grp-first Contrato
          th.amt.grp Declarado
          th.amt.grp Déb. sugerido
          th.amt.grp.grp-last Aceptado
          th Motor
          th Decisión
      tbody
        tr(v-if="filteredLines.length === 0")
          td.empty(:colspan="canEdit ? 10 : 9") Sin líneas
        tr(v-for="line in filteredLines" :key="line._id" :class="{ 'row--selected': isLineSelected(line._id) }")
          //- UC-REG-230 — checkbox por línea (excepto anuladas)
          td.sel(v-if="canEdit")
            el-checkbox(
              v-if="line.status !== 'voided'"
              :value="isLineSelected(line._id)"
              @change="toggleLine(line._id)"
            )
          td.n {{ line.lineNumber }}
          //- Afiliado — UC-REG-209: nombre + DNI + Nº afiliado
          td
            .p-name(v-if="memberName(line)") {{ memberName(line) }}
            .p-meta(v-if="line.declaredDni") DNI {{ line.declaredDni }}
            .p-meta(v-if="line.declaredMemberNumber") Afil. {{ line.declaredMemberNumber }}
            .p-meta.muted(v-if="!memberName(line) && !line.declaredDni && !line.declaredMemberNumber")
              | — sin datos ({{ line.declaredMemberIdentifier || '' }})
          //- Prestación — UC-REG-209/212: código nomenclador + denominación.
          //- matchedPractice puede venir como ObjectId sin populate, así que
          //- usamos declaredCode/declaredName que siempre están.
          td
            .c-codes
              span.c-int {{ line.declaredCode || '—' }}
              span.c-prov(v-if="line.providerServiceCode") {{ line.providerServiceCode }}
            .c-name {{ line.declaredName || (line.matchedPractice && line.matchedPractice.name) || '—' }}
            .c-date {{ formatShortDate(line.performedAt) }}
            .c-hint(v-if="line.rejectionReason") {{ line.rejectionReason }}
          //- UC-REG-209 — Contrato: contractAmount de la línea (setado por
          //- el motor al resolver el ContractItem vigente para la práctica).
          td.amt.grp.grp-first.contract-cell
            span(v-if="line.contractAmount != null") {{ formatMoney(line.contractAmount) }}
            span.muted(v-else) —
          td.amt.grp {{ formatMoney(line.declaredAmount) }}
          td.amt.grp.suggested {{ formatMoney(suggestedDebit(line)) }}
          td.amt.grp.grp-last(:class="{ 'amt--ok': line.acceptedAmount > 0, 'amt--err': line.acceptedAmount === 0 }")
            | {{ formatMoney(line.acceptedAmount) }}
          td
            span.chip(:class="motorChip[line.status]") {{ motorLabel[line.status] || line.status }}
            //- UC-REG-210 — Buscar match manual: sólo cuando el motor no
            //- matcheó auto pero pudo haber TRX previa que el operador conoce.
            a.match-link(
              v-if="canOfferMatch(line)"
              @click="openMatchModal(line)"
              title="Buscar TRX existente para vincular"
            ) Buscar match
          td
            //- UC-REG-175/187 — Chip prominente con el estado actual + link
            //- "Cambiar" que abre modal con las 4 opciones (Aceptar / Rechazar
            //- / Débito / Anular). Antes los 3 botones parecían acciones
            //- pendientes cuando el motor ya había decidido.
            .decision-cell
              span.decision-chip.decision-chip--void(v-if="decisionOf(line) === 'void'")
                i.el-icon-remove-outline
                | &nbsp;Anulada
              span.decision-chip.decision-chip--ok(v-else-if="decisionOf(line) === 'accept'")
                i.el-icon-check
                | &nbsp;Aceptado
              span.decision-chip.decision-chip--err(v-else-if="decisionOf(line) === 'reject'")
                i.el-icon-close
                | &nbsp;Rechazado
              span.decision-chip.decision-chip--debit(v-else)
                i.el-icon-warning-outline
                | &nbsp;Débito
              a.decision-change(
                v-if="canEdit"
                @click="openOverride(line, decisionOf(line))"
              ) Cambiar
              span.ov-tag(v-if="line.status === 'ok_manual_override'") manual

  //- Modal Override
  el-dialog(
    :visible.sync="overrideModalOpen"
    title="Cambiar decisión"
    width="480px"
    :close-on-click-modal="false"
  )
    .ov-modal(v-if="overrideLine")
      p.muted {{ overrideModalHeader }}
      //- UC-REG-178/189 — warning duro cuando accept está bloqueado.
      //-   El backend bloquea; acá lo aclaramos antes para que el operador
      //-   no pierda tiempo tipeando motivo. Mensaje distinto según el status.
      .ov-guard(v-if="isBlockedForAccept")
        i.el-icon-warning
        span(v-if="overrideLine.status === 'rejected_member'")
          strong  No se puede aceptar:
          | &nbsp;el afiliado declarado no existe en el sistema. Cargá el Member primero y reprocessá el submission. Podés Rechazar, Débito o Anular línea.
        span(v-else-if="overrideLine.status === 'rejected_duplicate'")
          strong  No se puede aceptar:
          | &nbsp;esta prestación ya fue liquidada en un submission previo (ver motivo en la línea). Si de verdad hay que reliquidar, anulá primero el submission original. Podés Rechazar, Débito o Anular línea acá.
      .ov-modal__field
        label.form-label Nueva decisión
        el-radio-group(v-model="overrideDecision")
          el-radio(label="accept" :disabled="isBlockedForAccept") Aceptar
          el-radio(label="reject") Rechazar
          el-radio(label="debit") Débito
          //- UC-REG-187 — Anular línea (mal cargada del archivo).
          //- Distinto de Rechazar: no cuenta como débito. Se excluye del cálculo.
          el-radio(label="void") Anular línea
      .ov-hint(v-if="overrideDecision === 'void'")
        i.el-icon-info
        span
          | &nbsp;Anular = "esta línea vino mal cargada, ignorala del cálculo". NO cuenta como débito ni como aceptada. La TRX creada por el pipeline se rechaza; si el SR era pre-existente vuelve a Pendiente.
      .ov-hint.ov-hint--muted(v-else-if="overrideDecision === 'debit'")
        i.el-icon-info
        span
          | &nbsp;Débito = hubo prestación (o intento) pero rechazás el monto (total o parcial). Es decisión financiera sobre algo real.
      .ov-modal__field(v-if="overrideDecision === 'debit'")
        label.form-label Monto a debitar
        el-input(v-model="overrideDebitAmount" type="number" size="small")
      .ov-modal__field
        label.form-label Motivo (obligatorio)
        el-input(
          v-model="overrideReason"
          type="textarea" :rows="3" size="small"
          placeholder="Ej: reclamo del prestador, doc adicional, autorización excepcional…"
        )
    div(slot="footer")
      el-button(@click="overrideModalOpen = false" size="small") Cancelar
      el-button(type="primary" @click="submitOverride" size="small") Aplicar

  //- Modal Anular
  el-dialog(
    :visible.sync="revertModalOpen"
    title="Anular liquidación"
    width="480px"
    :close-on-click-modal="false"
  )
    .ov-modal
      p.muted
        | Se anula la liquidación completa como si nunca se hubiera cargado. NO cuenta como débito — usá "Débito" solo cuando aceptás la prestación pero rechazás el monto.
        br
        br
        | Efecto: los montos aceptados/débitos vuelven a $0, las TRXs creadas por esta importación se rechazan, y las TRXs que ya existían vuelven a estado Pendiente. Auditable — queda registro del motivo.
      .ov-modal__field
        label.form-label Motivo (obligatorio)
        el-input(
          v-model="revertReason"
          type="textarea" :rows="3" size="small"
          placeholder="Ej: error de carga, mapping incorrecto, cambio de política…"
        )
    div(slot="footer")
      el-button(@click="revertModalOpen = false" size="small") Cancelar
      el-button(type="danger" @click="revert" :loading="revertLoading" size="small") Anular

  //- UC-REG-210 — Modal buscar match manual
  el-dialog(
    :visible.sync="matchModalOpen"
    title="Buscar match manual"
    width="680px"
    :close-on-click-modal="false"
    v-loading="matchLoading"
  )
    .match-modal(v-if="matchLine")
      p.muted El motor no encontró match automático. Elegí una TRX existente para vincular, o marcá la línea como prestación nueva.
      .match-ctx
        .match-ctx__item
          .lbl Afiliado
          .val {{ memberName(matchLine) || matchLine.patientFullName || '—' }}
          .val.muted(v-if="matchLine.declaredDni") DNI {{ matchLine.declaredDni }}
        .match-ctx__item
          .lbl Prestación
          .val.mono {{ matchLine.declaredCode }}
          .val.muted {{ matchLine.declaredName || (matchLine.matchedPractice && matchLine.matchedPractice.name) || '—' }}
        .match-ctx__item
          .lbl Fecha
          .val {{ formatShortDate(matchLine.performedAt) }}
        .match-ctx__item
          .lbl Declarado
          .val.mono {{ formatMoney(matchLine.declaredAmount) }}

      .match-empty(v-if="!matchLoading && matchCandidates.length === 0")
        | No hay prestaciones validadas de este prestador para esa fecha.
        br
        strong Se procesará como prestación ex-post
        | &nbsp;cuando aceptes el lote.

      .match-list(v-if="matchCandidates.length > 0")
        .match-list__head
          span.h-lbl Candidatas — mismo prestador, mismo día
          span.h-count {{ matchCandidates.length }} TRXs
        .match-cand(
          v-for="c in matchCandidates"
          :key="c._id"
          :class="{ 'is-selected': selectedCandidateId === c._id }"
          @click="selectedCandidateId = c._id"
        )
          .cand-radio
            span.radio-dot
          .cand-trx
            .code {{ c.transactionCode }}
            .time {{ formatShortTime(c.performedAt) }}
          .cand-body
            .name {{ (c.memberSnapshot && (c.memberSnapshot.lastName + ', ' + c.memberSnapshot.firstName)) || '—' }}
            .prac
              span.pcode {{ c.practiceSnapshot && c.practiceSnapshot.code }}
              | {{ c.practiceSnapshot && c.practiceSnapshot.name }}
            .meta
              span.chip.chip--info Validación directa
              span.amount {{ formatMoney(c.expectedAmount || (c.valorization && c.valorization.amount)) }}

    div(slot="footer" style="display: flex; gap: 8px; justify-content: flex-end;")
      el-button(size="small" @click="matchModalOpen = false") Cancelar
      el-button(
        size="small"
        type="danger"
        plain
        @click="rejectLineFromModal"
      ) Rechazar línea
      el-button(
        size="small"
        type="warning"
        plain
        @click="matchModalOpen = false"
      ) Es prestación nueva (ex-post)
      el-button(
        size="small"
        type="primary"
        :disabled="!selectedCandidateId"
        :loading="linkLoading"
        @click="submitManualMatch"
      ) Vincular con la seleccionada
</template>

<style lang="scss" scoped>
.subm-detail {
  font-family: "Plus Jakarta Sans", sans-serif;
}
.subm-detail__head {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 18px 22px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 12px;
  .back {
    margin-bottom: 8px;
  }
  .title {
    font-size: 16px;
    font-weight: 500;
    color: #002d5e;
    margin: 0;
  }
  .meta {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #6b7294;
    margin-top: 4px;
  }
  .dot {
    color: #cbd5e1;
  }
  .actions {
    display: flex;
    gap: 6px;
  }
}

.subm-detail__progress {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  p {
    margin: 0 0 8px;
  }
}
.muted {
  color: #94a3b8;
  font-size: 12px;
}

.subm-detail__failed,
.subm-detail__reverted {
  margin-bottom: 12px;
}

.subm-detail__stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 12px;
  .stat {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 12px 14px;
  }
  .stat__label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #94a3b8;
    font-weight: 500;
  }
  .stat__value {
    font-size: 20px;
    font-weight: 600;
    color: #002d5e;
    font-variant-numeric: tabular-nums;
    margin-top: 4px;
  }
  .stat__value--ok {
    color: #065f46;
  }
  .stat__value--err {
    color: #b91c1c;
  }
}

// UC-REG-180 Fase 2 — Tabs Prestaciones / Factura
.subm-detail__tabs {
  display: flex;
  gap: 0;
  border-bottom: 1px solid #e2e8f0;
  margin: 20px 0 16px 0;
}
.subm-detail__tab {
  padding: 10px 22px;
  font-size: 13px;
  color: #94a3b8;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  font-weight: 600;
  transition: color 0.15s, border-color 0.15s;
  &:hover {
    color: #002d5e;
  }
  &.is-active {
    color: #002d5e;
    border-bottom-color: #002d5e;
  }
}
.subm-detail__tab-count {
  color: #94a3b8;
  font-weight: 400;
  font-size: 12px;
}

.subm-detail__filters {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

/* UC-REG-230 — Barra de acción para aceptar en bloque */
.subm-detail__bulkbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  padding: 8px 14px;
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  border-radius: 10px;
}
.subm-detail__bulkbar-count {
  font-size: 12.5px;
  font-weight: 600;
  color: #3730a3;
}
.subm-detail__table td.sel,
.subm-detail__table th.sel {
  width: 34px;
  text-align: center;
  padding-left: 6px;
  padding-right: 6px;
}
.subm-detail__table tr.row--selected td {
  background: #f5f7ff;
}

.subm-detail__wrap {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow-x: auto;
}
.subm-detail__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  min-width: 1050px;
  th {
    text-align: left;
    padding: 9px 10px;
    font-size: 9.5px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #94a3b8;
    font-weight: 500;
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
  }
  th.amt {
    text-align: right;
  }
  th.n {
    width: 30px;
    text-align: center;
  }
  th.grp {
    background: #fafcff;
  }
  th.grp-first {
    border-left: 2px solid #dbeafe;
  }
  th.grp-last {
    border-right: 2px solid #dbeafe;
  }
  td {
    padding: 11px 10px;
    border-bottom: 0.5px solid #f1f5f9;
    color: #1a1f36;
    vertical-align: middle;
  }
  td.n {
    color: #94a3b8;
    text-align: center;
  }
  td.amt {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
  td.grp {
    background: #fafcff;
  }
  td.grp-first {
    border-left: 2px solid #dbeafe;
  }
  td.grp-last {
    border-right: 2px solid #dbeafe;
  }
  td.empty {
    text-align: center;
    color: #94a3b8;
    padding: 30px;
  }
  td.suggested {
    color: #94a3b8;
    font-style: italic;
  }
}
.amt--ok {
  color: #065f46;
  font-weight: 500;
}
.amt--err {
  color: #b91c1c;
}

.p-meta {
  font-size: 11px;
  color: #6b7294;
}
.c-codes {
  display: flex;
  align-items: center;
  gap: 6px;
}
.c-label {
  font-size: 8.5px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #94a3b8;
  font-weight: 500;
}
.c-int {
  font-size: 13px;
  font-weight: 600;
  color: #002d5e;
  font-variant-numeric: tabular-nums;
}
.c-int.muted {
  color: #cbd5e1;
  font-weight: 400;
}
.c-prov {
  font-variant-numeric: tabular-nums;
  font-size: 10.5px;
  padding: 1px 7px;
  border-radius: 4px;
  font-weight: 500;
  background: #ccfbf1;
  color: #115e59;
}
.c-date {
  font-size: 10.5px;
  color: #94a3b8;
  margin-top: 2px;
}
.c-hint {
  font-size: 10.5px;
  color: #94a3b8;
  font-style: italic;
  margin-top: 2px;
}

.chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 500;
  white-space: nowrap;
}
.chip--ok {
  background: #d1fae5;
  color: #065f46;
}
.chip--warn {
  background: #fef3c7;
  color: #92400e;
}
.chip--err {
  background: #fee2e2;
  color: #b91c1c;
}
.chip--info {
  background: #dbeafe;
  color: #1e40af;
}
.chip--neutral {
  background: #f1f5f9;
  color: #475569;
}

// UC-REG-175 — Decisión = chip actual + link "Cambiar". Reemplaza el
// tri-toggle que se veía como acciones pendientes.
.decision-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.decision-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 600;
  border: 1px solid transparent;
  i {
    font-size: 12px;
  }
  &--ok {
    background: #ecfdf5;
    color: #065f46;
    border-color: #a7f3d0;
  }
  &--err {
    background: #fef2f2;
    color: #991b1b;
    border-color: #fecaca;
  }
  &--debit {
    background: #fef3c7;
    color: #92400e;
    border-color: #fde68a;
  }
  &--void {
    background: #f1f5f9;
    color: #64748b;
    border-color: #cbd5e1;
    text-decoration: line-through;
  }
}
.decision-change {
  font-size: 11px;
  color: #6b7294;
  cursor: pointer;
  text-decoration: none;
  &:hover {
    color: #002d5e;
    text-decoration: underline;
  }
}
.ov-tag {
  background: #ede9fe;
  padding: 1px 6px;
  border-radius: 3px;
  font-size: 10px;
  font-weight: 500;
  color: #5b21b6;
}

.ov-modal__field {
  margin-top: 12px;
}
/* UC-REG-187 — hint informativo sobre débito vs anular en el modal override */
.ov-hint {
  margin-top: 8px;
  padding: 8px 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  font-size: 11.5px;
  color: #1e40af;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  line-height: 1.4;
  i {
    color: #3b82f6;
    margin-top: 1px;
    font-size: 13px;
  }
  &--muted {
    background: #f8fafc;
    border-color: #e2e8f0;
    color: #475569;
    i {
      color: #94a3b8;
    }
  }
}

/* UC-REG-196 — banner reviewing + botón ACEPTAR más prominente */
.subm-detail__reviewing {
  margin-bottom: 16px;
}
.subm-detail__accept-btn.el-button {
  padding: 8px 20px;
  font-weight: 700;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 6px rgba(0, 45, 94, 0.15);
}

/* UC-REG-178 Fase 1 — warning cuando accept está bloqueado */
.ov-guard {
  margin-top: 10px;
  padding: 10px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  color: #991b1b;
  font-size: 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  line-height: 1.4;
  i {
    font-size: 14px;
    margin-top: 1px;
  }
}
.form-label {
  display: block;
  font-size: 12px;
  color: #6b7294;
  font-weight: 500;
  margin-bottom: 6px;
}

/* UC-REG-210 — Buscar match manual */
.match-link {
  display: inline-block;
  margin-left: 8px;
  font-size: 11px;
  color: #1e3a8a;
  cursor: pointer;
  text-decoration: underline;
}
.match-link:hover {
  color: #4c6cf7;
}

.match-modal {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.match-modal .muted {
  color: #6b7294;
  font-size: 12.5px;
}

.match-ctx {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  padding: 12px 14px;
  background: #fafbfd;
  border: 1px solid #e4e8ef;
  border-radius: 10px;
  font-size: 12px;
}
.match-ctx__item .lbl {
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #94a3b8;
  font-weight: 600;
  font-size: 10px;
  margin-bottom: 2px;
}
.match-ctx__item .val {
  color: #1a1f36;
  font-weight: 500;
}
.match-ctx__item .val.mono {
  font-family: monospace;
  color: #1e3a8a;
  font-weight: 600;
}
.match-ctx__item .val.muted {
  color: #94a3b8;
  font-weight: 400;
  font-size: 11px;
}

.match-empty {
  padding: 24px 20px;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
  background: #fafbfd;
  border: 1px dashed #e4e8ef;
  border-radius: 10px;
}
.match-empty strong {
  color: #1a1f36;
}

.match-list__head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 10px;
}
.match-list__head .h-lbl {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  font-weight: 600;
}
.match-list__head .h-count {
  font-size: 11px;
  color: #94a3b8;
}

.match-cand {
  display: grid;
  grid-template-columns: 32px 90px 1fr;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid #e4e8ef;
  border-radius: 10px;
  margin-bottom: 8px;
  background: white;
  cursor: pointer;
  transition: border-color 120ms, background 120ms;
}
.match-cand:hover {
  border-color: #1e3a8a;
  background: #eef2ff;
}
.match-cand.is-selected {
  border-color: #1e3a8a;
  background: #eef2ff;
  box-shadow: 0 0 0 3px rgba(30, 58, 138, 0.08);
}
.cand-radio {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 2px;
}
.radio-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #cdd4de;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 120ms;
}
.match-cand.is-selected .radio-dot {
  border-color: #1e3a8a;
}
.match-cand.is-selected .radio-dot::after {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #1e3a8a;
}
.cand-trx .code {
  font-family: monospace;
  font-weight: 600;
  color: #1e3a8a;
  font-size: 12.5px;
}
.cand-trx .time {
  color: #94a3b8;
  font-size: 11px;
  margin-top: 2px;
}
.cand-body .name {
  font-weight: 600;
  color: #1a1f36;
  font-size: 13px;
}
.cand-body .prac {
  color: #475569;
  font-size: 12px;
}
.cand-body .prac .pcode {
  font-family: monospace;
  color: #1e3a8a;
  font-weight: 500;
  margin-right: 6px;
}
.cand-body .meta {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 4px;
}
.cand-body .amount {
  font-family: monospace;
  font-weight: 600;
  color: #1a1f36;
  font-size: 12px;
}
</style>
